import React, { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  ModalText,
  ButtonsContainer,
  ConfirmButton,
  CancelButton,
} from "./ConfirmActionModal.styled";

interface ConfirmActionModalProps {
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  confirmButtonColor?: "primary" | "error" | "success";
  icon?: string;
}

const ConfirmActionModalContent: React.FC<ConfirmActionModalProps> = ({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  confirmButtonColor = "primary",
  icon,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
    } catch (error) {
      console.error("Error confirming action:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContentContainer>
      {icon && (
        <Box sx={{ mb: 3 }}>
          <img src={icon} alt="" width={138} height={120} />
        </Box>
      )}
      <ModalTitle>{title}</ModalTitle>
      <ModalText>{message}</ModalText>
      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onCancel}
          disabled={isLoading}
        >
          {cancelText || t("common.cancel")}
        </CancelButton>
        <ConfirmButton
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          color={confirmButtonColor}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            confirmText
          )}
        </ConfirmButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default ConfirmActionModalContent;
